import React, { useEffect, useState } from 'react';
import { getOrders, updateOrderStatus } from '../../api/inventory';
import { OrderStatus } from 'api/inventory-types';

const OrderList = () => {
    const [orders, setOrders] = useState<any[]>([]);
    const [filter, setFilter] = useState<OrderStatus | 'all'>('all');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        const items = await getOrders();
        setOrders(items);
    };

    const handleStatusUpdate = async (orderId: string, newStatus: OrderStatus) => {
        const response = await updateOrderStatus(orderId, newStatus);
        if (response.status === 200) {
            loadOrders();
        }
    };

    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [orderToCancel, setOrderToCancel] = useState<string | null>(null);

    const handleCancelClick = (orderId: string) => {
        setOrderToCancel(orderId);
        setShowCancelDialog(true);
    };

    const handleConfirmCancel = async () => {
        if (orderToCancel) {
            await handleStatusUpdate(orderToCancel, OrderStatus.CANCELLED);
            setShowCancelDialog(false);
            setOrderToCancel(null);
        }
    };

    const [showConfirmOrderSentDialog, setShowConfirmOrderSentDialog] = useState(false);
    const [orderToConfirmSent, setOrderToConfirmSent] = useState<string | null>(null);

    const handleSentOrder = async (orderId: string) => {
        const order = orders.find(o => o.instanceId === orderId);
        if (!order) return;

        setOrderToConfirmSent(orderId);
        setShowConfirmOrderSentDialog(true);
    };

    const handleConfirmOrderSent = async () => {
        if (orderToConfirmSent) {
            await handleStatusUpdate(orderToConfirmSent, OrderStatus.SENT);
            setShowConfirmOrderSentDialog(false);
            setOrderToConfirmSent(null);
        }
    };

    const CancelDialog = () => {
        if (!showCancelDialog) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg">
                    <h3 className="text-lg font-bold mb-4">Cancel Order</h3>
                    <p className="mb-4">Are you sure you want to cancel this order?</p>
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={handleCancelDialogClose}
                            className="px-4 py-2 bg-gray-200 rounded"
                        >
                            No
                        </button>
                        <button
                            onClick={handleConfirmCancel}
                            className="px-4 py-2 bg-red-500 text-white rounded"
                        >
                            Yes, Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const ConfirmOrderSentDialog = () => {
        if (!showConfirmOrderSentDialog) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-lg w-full max-w-2xl mx-4">
                    <h3 className="text-lg font-bold mb-4">Send order</h3>
                    {orderToConfirmSent && (
                        <div className="mb-6 grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <p>
                                    <span className="font-semibold block">Name:</span>
                                    {orders.find(o => o.instanceId === orderToConfirmSent)?.name} {orders.find(o => o.instanceId === orderToConfirmSent)?.surname}
                                </p>
                                <p>
                                    <span className="font-semibold block">Email:</span>
                                    {orders.find(o => o.instanceId === orderToConfirmSent)?.email}
                                </p>
                            </div>
                            <div>
                                <p>
                                    <span className="font-semibold block">Address:</span>
                                    {orders.find(o => o.instanceId === orderToConfirmSent)?.address}
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end space-x-4">
                        <button onClick={handleConfirmOrderSentClose} className="bg-gray-200 px-6 py-2 rounded hover:bg-gray-300">
                            Cancel
                        </button>
                        <button onClick={handleConfirmOrderSent} className="bg-green-500 text-white px-4 py-2 rounded">Confirm</button>
                    </div>
                </div>
            </div>
        );
    };

    
    const handleCancelDialogClose = () => {
        setShowCancelDialog(false);
        setOrderToCancel(null);
    };

    const handleConfirmOrderSentClose = () => {
        setShowConfirmOrderSentDialog(false);
        setOrderToConfirmSent(null);
    };

    const filteredOrders = filter === 'all' 
        ? orders 
        : orders.filter(order => order.status === filter);

    const sortedOrders = [...filteredOrders].sort((a, b) => {
        const dateA = new Date(a.createdDate).getTime();
        const dateB = new Date(b.createdDate).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });

    const toggleSort = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    return (
        <div className="container mx-auto px-2 py-6">
            <div className="flex justify-between items-center mb-6">
                <CancelDialog />
                <ConfirmOrderSentDialog />
                <h2 className="text-2xl font-bold">Orders</h2>
                <div className="space-x-2">
                    <button
                        onClick={toggleSort}
                        className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                    >
                        Date {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilter(OrderStatus.ORDERED)}
                        className={`px-4 py-2 rounded ${filter === OrderStatus.ORDERED ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Ordered
                    </button>
                    <button
                        onClick={() => setFilter(OrderStatus.SENT)}
                        className={`px-4 py-2 rounded ${filter === OrderStatus.SENT ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Sent
                    </button>
                    <button
                        onClick={() => setFilter(OrderStatus.CANCELLED)}
                        className={`px-4 py-2 rounded ${filter === OrderStatus.CANCELLED ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    >
                        Cancelled
                    </button>
                </div>
            </div>
            <div className="bg-white shadow-md rounded-lg">
                <table className="w-full table-fixed">
                    <thead>
                        <tr>
                            <th className="px-3 py-3 border-b w-20">Order ID</th>
                            <th className="px-3 py-3 border-b w-24 cursor-pointer hover:bg-gray-50" onClick={toggleSort}>
                                Date {sortDirection === 'asc' ? '↑' : '↓'}
                            </th>
                            <th className="px-3 py-3 border-b w-32">Customer</th>
                            <th className="px-3 py-3 border-b w-40">Address</th>
                            <th className="px-3 py-3 border-b w-40">Email</th>
                            <th className="px-3 py-3 border-b w-28">Product ID</th>
                            <th className="px-3 py-3 border-b w-20">Status</th>
                            <th className="px-3 py-3 border-b">Data</th>
                            <th className="px-3 py-3 border-b w-40">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedOrders.map(order => (
                            <tr key={order.instanceId}>
                                <td className="px-3 py-3 border-b text-sm">{order.instanceId}</td>
                                <td className="px-3 py-3 border-b text-sm">{new Date(order.createdDate).toLocaleDateString()}</td>
                                <td className="px-3 py-3 border-b text-sm">{order.name} {order.surname}</td>
                                <td className="px-3 py-3 border-b text-sm">{order.address}</td>
                                <td className="px-3 py-3 border-b text-sm">{order.email}</td>
                                <td className="px-3 py-3 border-b text-sm">{order.productInventoryItem.productTypeId}</td>
                                <td className="px-3 py-3 border-b text-sm">{order.status}</td>
                                <td className="px-3 py-3 border-b text-sm">
                                    <div className="space-y-1">
                                        {Object.entries(order.productInventoryItem.instanceData || {}).map(([key, value]) => (
                                            <div key={key} className="flex gap-2">
                                                <span className="font-medium">{key}:</span>
                                                <span className="font-mono">{JSON.stringify(value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-3 py-3 border-b text-center">
                                    {order.status === OrderStatus.ORDERED && (
                                        <div className="flex justify-center space-x-4">
                                            <button
                                                onClick={() => handleSentOrder(order.instanceId)}
                                                className="bg-green-500 text-white px-4 py-2 rounded"
                                            >
                                                Send
                                            </button>
                                            <button
                                                onClick={() => handleCancelClick(order.instanceId)}
                                                className="bg-red-500 text-white px-4 py-2 rounded"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OrderList; 